import React from 'react';
import {
  BulkUpdate,
  BulkUpdateOutcomeRecord,
  ChannelManagerSyncStatus
} from '../../../models/unit-types.model';

const RENTALS_UNITED_STATUS_LABELS: Record<ChannelManagerSyncStatus, string> = {
  ok: 'Synced',
  failed: 'Failed',
  skipped: 'Not synced'
};

/** Units worth showing individually: anything that did not fully succeed on both sides. */
const isProblemRecord = (record: BulkUpdateOutcomeRecord) =>
  record.birdnest === 'failed' || record.rentalsUnited === 'failed';

export interface BulkUpdateResultPanelProps {
  data: {
    result: BulkUpdate;
  };
}

/**
 * Shows what a bulk unit update actually did, per side: how many units saved in
 * Birdnest and how many reached Rentals United, then names every unit that failed
 * on either side. Shared by the unit-level and project-level bulk actions so a
 * failure is reported the same way wherever it happens.
 */
export const BulkUpdateResultPanel: React.FC<BulkUpdateResultPanelProps> = ({ data }) => {
  const { result } = data;
  const birdnestSummary = result?.birdnest;
  const rentalsUnitedSummary = result?.rentalsUnited;
  const problemRecords = (result?.records || []).filter(isProblemRecord);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="text-[1.2rem] font-gilroy-medium uppercase tracking-[0.07em] text-gray-500 mb-2">
            Birdnest
          </div>
          {birdnestSummary ? (
            <div className="flex items-baseline gap-5">
              <div>
                <span className="text-[2.4rem] leading-none font-gilroy-bold text-gray-900">
                  {birdnestSummary.success}
                </span>
                <span className="ml-1.5 text-[1.3rem] text-gray-500">updated</span>
              </div>
              <div>
                <span
                  className={
                    'text-[2.4rem] leading-none font-gilroy-bold ' +
                    (birdnestSummary.failed > 0 ? 'text-action-alert' : 'text-gray-400')
                  }
                >
                  {birdnestSummary.failed}
                </span>
                <span className="ml-1.5 text-[1.3rem] text-gray-500">failed</span>
              </div>
            </div>
          ) : (
            <div className="text-[1.3rem] text-gray-500">
              This server build does not report per-unit results yet.
            </div>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="text-[1.2rem] font-gilroy-medium uppercase tracking-[0.07em] text-gray-500 mb-2">
            Rentals United
          </div>
          {rentalsUnitedSummary ? (
            <div className="flex items-baseline gap-5 flex-wrap">
              <div>
                <span className="text-[2.4rem] leading-none font-gilroy-bold text-gray-900">
                  {rentalsUnitedSummary.success}
                </span>
                <span className="ml-1.5 text-[1.3rem] text-gray-500">synced</span>
              </div>
              <div>
                <span
                  className={
                    'text-[2.4rem] leading-none font-gilroy-bold ' +
                    (rentalsUnitedSummary.failed > 0 ? 'text-action-alert' : 'text-gray-400')
                  }
                >
                  {rentalsUnitedSummary.failed}
                </span>
                <span className="ml-1.5 text-[1.3rem] text-gray-500">failed</span>
              </div>
              <div>
                <span className="text-[2.4rem] leading-none font-gilroy-bold text-gray-400">
                  {rentalsUnitedSummary.skipped}
                </span>
                <span className="ml-1.5 text-[1.3rem] text-gray-500">not synced</span>
              </div>
            </div>
          ) : (
            <div className="text-[1.3rem] text-gray-500">
              This server build does not report Rentals United results yet.
            </div>
          )}
        </div>
      </div>

      {rentalsUnitedSummary && rentalsUnitedSummary.skipped > 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 mb-4 text-[1.3rem] leading-normal text-gray-600">
          &ldquo;Not synced&rdquo; means the unit is not linked to Rentals United, or its Birdnest
          update failed so nothing was sent. It is not a Rentals United error.
        </div>
      )}

      <div className="border border-gray-200 rounded-xl mb-6 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-white">
          <div className="text-[1.5rem] font-gilroy-bold text-gray-900">
            {problemRecords.length > 0 ? `Needs attention (${problemRecords.length})` : 'No failures'}
          </div>
          <div className="text-[1.2rem] text-gray-500">
            {problemRecords.length > 0
              ? 'These units failed on at least one side.'
              : 'Every unit updated on both sides.'}
          </div>
        </div>
        {problemRecords.length > 0 && (
          <div className="max-h-[260px] overflow-y-auto">
            {problemRecords.map((record) => (
              <div key={record.unitId} className="px-4 py-3 border-b border-gray-200 last:border-b-0">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-[1.4rem] font-gilroy-medium text-gray-900 truncate">
                    {record.unitName || record.unitId}
                  </div>
                  <div className="flex items-center gap-2 shrink-0 text-[1.1rem] font-gilroy-medium uppercase tracking-[0.05em]">
                    <span
                      className={
                        'rounded px-2 py-0.5 ' +
                        (record.birdnest === 'ok'
                          ? 'bg-gray-100 text-gray-500'
                          : 'bg-red-50 text-red-700')
                      }
                    >
                      BN {record.birdnest === 'ok' ? 'ok' : 'failed'}
                    </span>
                    <span
                      className={
                        'rounded px-2 py-0.5 ' +
                        (record.rentalsUnited === 'failed'
                          ? 'bg-red-50 text-red-700'
                          : 'bg-gray-100 text-gray-500')
                      }
                    >
                      RU {RENTALS_UNITED_STATUS_LABELS[record.rentalsUnited]}
                    </span>
                  </div>
                </div>
                {record.errorMessage && (
                  <div className="mt-1 text-[1.2rem] leading-normal text-gray-500 break-words">
                    {record.errorMessage}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default BulkUpdateResultPanel;
